import type { ProjectState } from '@/lib/types';
import { repairFloors } from '@/lib/floors';

// ── Export / import d'un projet (fichier .archifacil) ──────
// Sert a transferer un projet d'un environnement a l'autre (dev → prod), a le sauvegarder ou a le partager.

export const EXPORT_FORMAT = 'archifacil-project';
export const EXPORT_VERSION = 1;
export const EXPORT_EXTENSION = '.archifacil';
/** Taille max d'un fichier importe (l'image de fond peut peser plusieurs Mo) */
export const MAX_IMPORT_BYTES = 50 * 1024 * 1024;

export interface ProjectExportFile {
  format: typeof EXPORT_FORMAT;
  version: number;
  exportedAt: string;
  project: ProjectState;
}

export function buildProjectExport(project: ProjectState): ProjectExportFile {
  return { format: EXPORT_FORMAT, version: EXPORT_VERSION, exportedAt: new Date().toISOString(), project };
}

/** Nom de fichier sans caracteres problematiques (accents conserves) */
export function exportFileName(name: string): string {
  const base = name.replace(/[\\/:*?"<>|]+/g, '').replace(/\s+/g, '_').replace(/_+/g, '_').trim() || 'projet';
  return `${base}${EXPORT_EXTENSION}`;
}

/** Telecharge un texte JSON sous ce nom (navigateur) */
export function downloadJson(fileName: string, text: string): void {
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

/** Telecharge le projet sous forme de fichier .archifacil (navigateur) */
export function downloadProjectExport(project: ProjectState): void {
  downloadJson(exportFileName(project.name), JSON.stringify(buildProjectExport(project)));
}

/** Cle portant un identifiant (ou une reference) : id, floorId, hostWallId, selectedWallIds... */
const isIdKey = (key: string) => key === 'id' || /Id$/.test(key);
const isIdListKey = (key: string) => /Ids$/.test(key);

/**
 * Regenere tous les identifiants du projet et met a jour toutes les references (colonnes et JSON :
 * pieces, mobilier, jonctions...). Indispensable a l'import : les identifiants sont des cles primaires
 * globales, les reutiliser ecraserait le projet d'origine s'il existe deja dans la base cible.
 * Generique : toute cle `id` / `xxxId` / `xxxIds` est remappee, y compris pour les entites a venir.
 */
export function remapProjectIds<T>(project: T, newId: () => string = () => crypto.randomUUID()): T {
  const ids = new Map<string, string>();
  const collect = (v: unknown) => {
    if (Array.isArray(v)) { v.forEach(collect); return; }
    if (!v || typeof v !== 'object') return;
    const o = v as Record<string, unknown>;
    if (typeof o.id === 'string' && o.id && !ids.has(o.id)) ids.set(o.id, newId());
    Object.values(o).forEach(collect);
  };
  collect(project);

  const rewrite = (v: unknown, key = ''): unknown => {
    if (typeof v === 'string') return isIdKey(key) ? ids.get(v) ?? v : v;
    if (Array.isArray(v)) {
      return isIdListKey(key) ? v.map(x => (typeof x === 'string' ? ids.get(x) ?? x : rewrite(x))) : v.map(x => rewrite(x));
    }
    if (!v || typeof v !== 'object') return v;
    return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, rewrite(x, k)]));
  };
  return rewrite(project) as T;
}

/**
 * Lit un fichier d'export et renvoie le projet pret a etre cree : nouveaux identifiants, nom suffixe.
 * Accepte aussi l'ancien export brut de l'editeur (.pieuvrelec.json = ProjectState seul).
 * Leve une Error au message affichable si le fichier est invalide.
 */
export function prepareProjectImport(text: string): ProjectState {
  let data: unknown;
  try { data = JSON.parse(text); } catch { throw new Error('Fichier illisible : ce n\'est pas un export de projet.'); }
  if (!data || typeof data !== 'object') throw new Error('Fichier invalide.');

  const file = data as Partial<ProjectExportFile> & Partial<ProjectState>;
  let project: Partial<ProjectState>;
  if (file.format === EXPORT_FORMAT) {
    if (typeof file.version !== 'number' || file.version > EXPORT_VERSION) {
      throw new Error('Ce fichier vient d\'une version plus récente de l\'application : import impossible ici.');
    }
    project = file.project ?? {};
  } else if (Array.isArray(file.walls) || Array.isArray(file.floors)) {
    project = file;
  } else {
    throw new Error('Fichier invalide : ce n\'est pas un export de projet.');
  }
  if (!project || typeof project !== 'object' || !Array.isArray(project.walls ?? []) || !Array.isArray(project.floors ?? [])) {
    throw new Error('Fichier invalide : contenu du projet incomplet.');
  }

  return asNewProject(project, `${project.name || 'Sans titre'} (importé)`);
}

/**
 * Copie independante d'un projet, prete a etre creee : nouveaux identifiants (references remappees),
 * nom donne, tableaux manquants completes, etages references mais absents recrees.
 * Sert a l'import et au clonage.
 */
export function asNewProject(project: Partial<ProjectState>, name: string): ProjectState {
  const full: ProjectState = {
    ...(project as ProjectState),
    id: project.id || 'new',
    name,
    floors: project.floors ?? [],
    symbols: project.symbols ?? [],
    wires: project.wires ?? [],
    circuits: project.circuits ?? [],
    rooms: project.rooms ?? [],
    walls: project.walls ?? [],
    openings: project.openings ?? [],
    staircases: project.staircases ?? [],
    tremies: project.tremies ?? [],
    roofs: project.roofs ?? [],
  };
  // Etages references mais absents (ex. ancien « __rdc__ » partage) : recrees, sinon la base
  // cible refuserait les elements qui pointent vers eux
  return remapProjectIds(repairFloors(full, () => crypto.randomUUID()));
}

/** Nom d'un clone : « Maison (copie) » */
export const cloneName = (name: string) => `${name || 'Sans titre'} (copie)`;

/** Enregistre un nouveau projet en base (POST complet) ; leve une Error si refuse */
export async function createProject(project: ProjectState & { lastMode?: string }): Promise<void> {
  const res = await fetch('/api/projects', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(project),
  });
  if (!res.ok) throw new Error(`Création du projet refusée (${res.status})`);
}
